import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { postAbortBroadcast } from '../../services/AudioAnnouncementService';
import type { AbortBroadcastRequest, AbortAudioResponse } from '../../types/AudioAnnouncement';

const DEFAULT_ABORT_ERROR = 'Abort failed. Please try again.';

function getAbortErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error != null && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof obj.error === 'string' && obj.error) return obj.error;
  }
  if (typeof error === 'string' && error) return error;
  return DEFAULT_ABORT_ERROR;
}

type Result = UseMutationResult<AbortAudioResponse, unknown, AbortBroadcastRequest>;

function useAbortBroadcast(
  successCb: (res: AbortAudioResponse) => void,
  failureCb: (error: string) => void,
): Result {
  const queryClient = useQueryClient();

  return useMutation(postAbortBroadcast, {
    onSuccess: (data, variables) => {
      successCb(data);
      queryClient.invalidateQueries({
        queryKey: ['audio-announcement-send-history', variables.fleetId],
      });
      // Refetch search results so abort count and table state stay in sync when viewing search
      queryClient.invalidateQueries({ queryKey: ['broadcast-search'] });
    },
    onError: (error: unknown) => {
      failureCb(getAbortErrorMessage(error));
    },
  });
}

export default useAbortBroadcast;
